import { useMemo } from 'react';
import {
  airportPopularName,
  cityByAirport,
  localizedCityByAirport,
  metroAirportsOf,
  polaritiesOf,
  FlightTariffLegUnavailableError,
} from '@voll-intelligence/domain';
import {
  airportLabel,
  airportSwitchOf,
  FlightOfferStaleError,
  type FlightTariffView,
} from '@voll-intelligence/genui-contract';
import type {
  FaresRequestText,
  FlightAirportSwitchFn,
  FlightExplorerEnv,
  FlightExplorerPlaces,
  FlightTariffPort,
  OfferListEnv,
  RenderFlightDetail,
} from '@voll-intelligence/intelligence-ui-kit';
import { useGenUiConfig, type GenUiConfig } from '../../shared/ui/config.js';
import { flightExplorerCopy } from '../../shared/ui/flight_explorer_copy.js';
import { faresRequestCopy } from '../../shared/ui/fares_request_copy.js';
import { flightFaresCopy } from '../../shared/ui/flight_fares_copy.js';
import {
  airportSwitchView,
  FLIGHT_OFFER_PLACES,
  flightPolicyCopy,
} from '../../shared/ui/flight_offer_copy.js';
import { genUiI18n, useGenUiI18n, type GenUiLocale } from '../../shared/ui/i18n.js';
import { useGenUiTelemetry } from '../../shared/ui/telemetry.js';
import {
  useFlightTariffPort,
  type FlightTariffPort as GenUiFlightTariffPort,
} from './flight_tariff_port.js';
import { FlightDetailDrawerContainer } from './flight_detail_drawer_container.js';

/*
 * O que as listas e telas de voo do kit leem do genui-kit (#4997): os textos do
 * catálogo, o cadastro e o classificador do domain, o desvio de aeroporto do
 * contrato, a porta de tarifas e o drawer de detalhe com o mapa de assentos. Os
 * cards do chat, a tela de tarifas e o explorer ligam o kit por aqui.
 */

/** O desvio de aeroporto da volta (#4203) pela regra do contrato, já escrito. */
export const flightAirportSwitch: FlightAirportSwitchFn = (input) =>
  airportSwitchView(airportSwitchOf(input));

/** O drawer de detalhe do voo: as pernas pela porta do `GenUiConfig` e o mapa
 *  de assentos, que ficam aqui. */
export const renderFlightDetail: RenderFlightDetail = (detail, onClose) => (
  <FlightDetailDrawerContainer
    offerId={detail.offerId}
    summary={detail.summary}
    {...(detail.focus ? { focus: detail.focus } : {})}
    onClose={onClose}
  />
);

/** Os textos da tela de tarifas por pedido, memoizados por locale. */
export function useFaresRequestText(): FaresRequestText {
  const { locale } = useGenUiI18n();
  return useMemo(
    () => ({
      copy: flightFaresCopy(locale),
      request: faresRequestCopy(locale),
      classify: polaritiesOf,
      locale,
      places: FLIGHT_OFFER_PLACES,
    }),
    [locale],
  );
}

/** A porta de tarifas do host na forma do kit; `undefined` sem ela. */
function tariffPortOf(
  port: GenUiFlightTariffPort | null | undefined,
): FlightTariffPort<FlightTariffView> | undefined {
  if (!port) return undefined;
  return {
    get: (offerId: string) => port.getTariff(offerId),
    // Handle morto (409 offer_stale, #2332): a escada ganha fase própria.
    isStale: (error: unknown) => error instanceof FlightOfferStaleError,
    // Uma perna do par sem tarifa (409 leg_unavailable, #6000): a faixa troca o voo dela.
    unavailableLeg: (error: unknown) =>
      error instanceof FlightTariffLegUnavailableError ? error.leg : undefined,
  };
}

/** A porta de tarifas do `GenUiConfig` na forma do kit; `undefined` sem ela. */
export function useFlightTariff(): FlightTariffPort<FlightTariffView> | undefined {
  const port = useFlightTariffPort();
  return useMemo(() => tariffPortOf(port), [port]);
}

/** O ambiente da lista de ofertas do kit, com a telemetria do detalhe. */
export function useOfferListEnv(): OfferListEnv {
  const { locale } = useGenUiI18n();
  const telemetry = useGenUiTelemetry();
  const text = useFaresRequestText();
  return useMemo(
    () => ({
      text,
      policy: flightPolicyCopy(locale),
      airportSwitchOf: flightAirportSwitch,
      renderDetail: renderFlightDetail,
      onDetailOpened: () => {
        telemetry.track('drawer_opened', { kind: 'flight_detail' });
      },
    }),
    [text, locale, telemetry],
  );
}

/** O cadastro de aeroportos do domain na forma que o explorer do kit lê, com a
 *  cidade no idioma de quem lê. */
function flightExplorerPlaces(locale: GenUiLocale): FlightExplorerPlaces {
  return {
    ...FLIGHT_OFFER_PLACES,
    airportLabel,
    airportName: (iata) => airportPopularName(iata) ?? cityByAirport(iata) ?? iata,
    localizedCity: (iata) => localizedCityByAirport(iata, locale) ?? iata,
    metroAirports: metroAirportsOf,
  };
}

/**
 * O ambiente do explorer do kit montado de uma `GenUiConfig` (#4997): os textos
 * do catálogo no locale dela, o cadastro e o classificador do domain, o desvio de
 * aeroporto do contrato, o drawer de detalhe e as portas de tarifa, telemetria,
 * pedido offline e ditado, mais o slot da fila de chips. Função pura: a casca do
 * chat a chama com o config do contexto, e a bancada do web-chat com o dela.
 */
export function flightExplorerEnvOf(config: GenUiConfig): FlightExplorerEnv {
  const { locale } = genUiI18n(config.locale);
  const telemetry = config.telemetry;
  return {
    copy: flightExplorerCopy(locale),
    locale,
    places: flightExplorerPlaces(locale),
    classify: polaritiesOf,
    tariff: tariffPortOf(config.ports?.flightTariff),
    track: (event, props) => {
      telemetry?.track(event, props);
    },
    renderDetail: renderFlightDetail,
    airportSwitchOf: flightAirportSwitch,
    dictation: config.dictation,
    offlineRequest: config.offlineRequest,
    toolbarSlot: config.resultsToolbarSlot,
  };
}

/** O ambiente do explorer pelo `GenUiConfig` do contexto. */
export function useFlightExplorerEnv(): FlightExplorerEnv {
  const config = useGenUiConfig();
  return useMemo(() => flightExplorerEnvOf(config), [config]);
}
